import { GrahpQLTrackingRequester, type TrackingGraphQL, type TrackingSpeedEvents } from '@cobliteam/api-lib';
import type { VehicleTrackingSegmentType } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { EventTrigger, Media, MediaType, TimestampInMillis, UUID } from '@cobliteam/common-types';
import { type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';

// Lazy instantiation to avoid circular dependency issues
const getGraphQLTrackingRequester = () => new GrahpQLTrackingRequester();

const QUERY_KEY = 'tracking';

export type TrackingSegmentRequest = {
  mediaId?: string;
  vehicleId: UUID;
  startTime: TimestampInMillis;
  endTime: TimestampInMillis;
  segmentStart: TimestampInMillis;
  segmentEnd: TimestampInMillis;
  allowUnfinishedStep: boolean;
  match?: boolean;
  eventTriggers?: EventTrigger[];
  mediaTypes?: MediaType[];
  enabled?: boolean;
};

const useTracking = () => {
  const getTrackingEssencialInfo = (vehicleId: UUID, startTimestamp: TimestampInMillis, endTimestamp: TimestampInMillis, useMapMatch: boolean, enabled = false) => {
    return useQuery<TrackingGraphQL>({
      queryKey: [`${QUERY_KEY}-stops`, vehicleId, startTimestamp, endTimestamp, useMapMatch],
      queryFn: () => getGraphQLTrackingRequester().getTrackingEssencialInfo(vehicleId, startTimestamp, endTimestamp, useMapMatch),
      refetchOnMount: false,
      enabled,
    });
  };

  const getTrackingSpeedEvents = (vehicleId: UUID, startTimestamp: TimestampInMillis, endTimestamp: TimestampInMillis, useMapMatch: boolean) => {
    return useQuery<TrackingSpeedEvents>({
      queryKey: [`${QUERY_KEY}-speed-events`, vehicleId, startTimestamp, endTimestamp, useMapMatch],
      queryFn: () => getGraphQLTrackingRequester().getTrackingSpeedEvents(vehicleId, startTimestamp, endTimestamp, useMapMatch),
      refetchOnMount: false,
      select(data) {
        return {
          ...data,
          roadSpeedEvents: data.roadSpeedEvents.map((event) => ({ ...event, type: 'road' as const })),
          vehicleSpeedEvents: data.vehicleSpeedEvents.map((event) => ({ ...event, type: 'vehicle' as const })),
        };
      },
    });
  };

  return {
    getTrackingEssencialInfo,
    getTrackingSpeedEvents,
  };
};

const useGetTracking = (
  vehicleId: UUID,
  startTimestamp: TimestampInMillis,
  endTimestamp: TimestampInMillis,
  useMapMatch: boolean,
  options?: Partial<UseQueryOptions<TrackingGraphQL>>
) => {
  return useQuery<TrackingGraphQL>({
    queryKey: [QUERY_KEY, vehicleId, startTimestamp, endTimestamp, useMapMatch],
    queryFn: () => getGraphQLTrackingRequester().getFullTrackingInfo(vehicleId, startTimestamp, endTimestamp, useMapMatch),
    ...options,
  });
};

const useGetTrackingSegment = ({
  vehicleId,
  startTime,
  endTime,
  segmentStart,
  segmentEnd,
  allowUnfinishedStep,
  match = true,
  eventTriggers = [],
  mediaTypes = [],
  enabled = true,
}: TrackingSegmentRequest) => {
  return useQuery<Omit<VehicleTrackingSegmentType, 'mediaEvents'> & { mediaEvents: Media[] }>({
    queryKey: [`${QUERY_KEY}-route-segment`, vehicleId, segmentStart, segmentEnd],
    queryFn: () =>
      getGraphQLTrackingRequester().getVehicleTrackingSegment(vehicleId, startTime, endTime, segmentStart, segmentEnd, allowUnfinishedStep, match, eventTriggers, mediaTypes),
    refetchOnMount: false,
    retry: false,
    enabled,
  });
};

const useGetTrackingSegments = (requests: TrackingSegmentRequest[]) => {
  return useQueries({
    queries: requests.map(
      ({ mediaId, vehicleId, startTime, endTime, segmentStart, segmentEnd, allowUnfinishedStep, match = true, eventTriggers = [], mediaTypes = [], enabled = true }) => ({
        queryKey: [`${QUERY_KEY}-media-route-segment`, vehicleId, segmentStart, segmentEnd],
        queryFn: async () => {
          const response = await getGraphQLTrackingRequester().getVehicleTrackingSegment(
            vehicleId,
            startTime,
            endTime,
            segmentStart,
            segmentEnd,
            allowUnfinishedStep,
            match,
            eventTriggers,
            mediaTypes
          );
          return { mediaId, ...response };
        },
        refetchOnMount: false,
        retry: false,
        enabled,
      })
    ),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((data) => data !== undefined),
        pending: results.some((result) => result.isPending),
      };
    },
  });
};

export default useTracking;
export { useGetTracking, useGetTrackingSegment, useGetTrackingSegments };
